import React from "react";
import { motion } from "framer-motion";
import Button from "./Button";
import { handwritten } from "./editorial";
import { WHATSAPP_COMMUNITY_URL } from "../lib/appLinks";

type DispatchItem = { emoji: string; title: string; meta: string; bg: string };

// Placeholder dispatch preview — stand-in while we hold back full app screenshots.
const dispatch: DispatchItem[] = [
  {
    emoji: "☕",
    title: "Coffee at Monmouth",
    meta: "Wed 10am · Covent Garden",
    bg: "var(--color-paper-salmon)",
  },
  {
    emoji: "🚶",
    title: "Hackney Sunday walk",
    meta: "Sun 9am · Hackney Marshes",
    bg: "var(--color-paper-mint)",
  },
  {
    emoji: "🏐",
    title: "Battersea volleyball",
    meta: "Tue 6:30pm · Battersea Park",
    bg: "var(--color-paper-tan)",
  },
  {
    emoji: "🏃",
    title: "Sunday morning run",
    meta: "Sun 9am · Regent's Park",
    bg: "var(--color-paper-salmon)",
  },
];

const Newsletter: React.FC = () => {
  return (
    <section
      id="newsletter"
      className="py-24 px-6 md:px-12 max-w-[1400px] mx-auto"
    >
      <div className="flex items-center gap-4 mb-10 max-w-3xl">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-salmon whitespace-nowrap">
          The invitation
        </span>
        <span className="flex-1 h-px bg-ink/15" />
      </div>
      <div className="relative rounded-3xl border border-ink/15 bg-paper-salmon overflow-hidden p-8 md:p-16 lg:p-24 flex flex-col md:flex-row items-center justify-between gap-16 transition-all duration-500 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)]">
        {/* Content */}
        <div className="relative z-10 max-w-xl w-full">
          <p
            className="text-3xl md:text-4xl text-salmon -rotate-2 mb-3"
            style={handwritten}
          >
            one last thing...
          </p>

          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6 leading-[1.05]">
            Get the weekly{" "}
            <span className="font-serif italic text-salmon">dispatch.</span>
          </h2>

          <p className="text-gray-600 text-lg md:text-xl leading-relaxed mb-8 max-w-md">
            A short weekly digest of the best loops in London: coffees, walks,
            dinners, runs, picnics. Skim it Sunday, plan your week.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 items-start max-w-md">
            <Button
              variant="primary"
              size="lg"
              isMagnetic={false}
              className="h-14 px-10 shrink-0"
              onClick={() =>
                window.open(
                  WHATSAPP_COMMUNITY_URL,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              Join the Community
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 mt-6">
            <p className="text-xs text-gray-400">
              No spam, just vibes. Unsubscribe anytime.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/letsloop.app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-salmon transition-colors"
              >
                Instagram
              </a>
            </div>
          </div>
        </div>

        {/* Visual: stylised "weekly dispatch" preview (placeholder while full app screenshots are held back) */}
        <div className="relative w-full md:w-[480px] flex items-center justify-center mt-8 md:mt-0">
          {/* Subtle floating glow */}
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.25, 0.45, 0.25] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 z-0"
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-salmon/20 rounded-full blur-3xl" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30, rotate: -3 }}
            whileInView={{ opacity: 1, y: 0, rotate: -2 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            whileHover={{ rotate: 0 }}
            className="relative z-10 w-full max-w-sm bg-canvas rounded-3xl border border-ink/15 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] p-6 md:p-7 transform-gpu"
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-salmon">
                This week near you
              </p>
              <span className="text-xs text-gray-400">Sunday</span>
            </div>
            <ul className="flex flex-col divide-y divide-ink/10">
              {dispatch.map((d) => (
                <li key={d.title} className="flex items-center gap-3 py-3">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl"
                    style={{ backgroundColor: d.bg }}
                  >
                    {d.emoji}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink truncate">
                      {d.title}
                    </p>
                    <p className="text-xs text-gray-500">{d.meta}</p>
                  </div>
                  <span className="shrink-0 text-[11px] font-bold uppercase tracking-wider text-salmon">
                    Loop in
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
